// App.js

import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "./hooks/useAuth";
import { useUIHelpers } from "./hooks/useUIHelpers"; // Import the UI helpers hook
import { componentMap, roles } from "./routes"; // Import componentMap and roles
import Login from "./view/Login";
import NotFound from "./components/Layouts/NotFound";
import UnderMaintenance from "./components/Layouts/UnderMaintenance";
import { useEffect, useState } from "react";
import axios from "axios";
import CustomLoader from "./components/CustomLoader";
import { useDispatch, useSelector } from "react-redux";
import APICaller, { resetConnectionErrorFlag } from "./utils/APICaller";
import { ercount, ncount } from "./store/actions";
import { message } from "antd";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const storeRole = useSelector((state) => state.auth.role);
  const storeToken = useSelector((state) => state.auth.accessToken);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [serverStatus, setServerStatus] = useState("up");
  const [isCheckingServerStatus, setIsCheckingServerStatus] = useState(true);

  // Use the custom hook for authentication and UI helpers
  const { loading, isLoggedIn, userRole, accessToken } = useAuth(
    navigate,
    location,
  );

  // Get the accessible routes based on the user's role
  const hasAccess = (role) => roles[role] || [];
  const accessibleRoutes = userRole ? hasAccess(userRole) : [];

  const expenseRequestsCount = () => {
    const endpoint = "/expenseRequestsCount";
    const method = "POST";
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn,
    )
      .then((response) => {
        dispatch(ercount({ ercount: response?.count }));
      })
      .catch((error) => {});
  };

  const notificationCount = () => {
    const endpoint = "/notificationCount";
    const method = "POST";
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn,
    )
      .then((response) => {
        dispatch(ncount({ ncount: response?.count }));
      })
      .catch((error) => {
        if (error.data && error.data.message) {
          message.error(error.data.message);
        }
      });
  };

  useEffect(() => {
    if (isLoggedIn && serverStatus === "Server is running") {
      // Run the function immediately
      if (storeRole !== process.env.REACT_APP_EROLE) {
        expenseRequestsCount();
        notificationCount();
      }
      const intervalId = setInterval(() => {
        if (
          storeRole !== process.env.REACT_APP_EROLE &&
          serverStatus === "Server is running"
        ) {
          expenseRequestsCount();
          notificationCount();
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [storeRole, storeToken, isLoggedIn, serverStatus]);

  // Call the UI helpers hook
  useUIHelpers();

  // Function to check server status (whether the backend is up or not)
  const checkMaintenanceStatus = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND + "/checkServerStatus",
      );
      setServerStatus(response.data.status);

      // If the server is down or in maintenance, navigate to the /underMaintenance route
      if (response.data.status === "Error") {
        setIsUnderMaintenance(true);
      } else {
        setIsUnderMaintenance(false);
      }
    } catch (error) {
      setServerStatus("Error");
      setIsUnderMaintenance(true);
    } finally {
      setIsCheckingServerStatus(false); // Finished checking server status
    }
  };

  // Run once on mount
  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  // Continuous health check every 10 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkMaintenanceStatus();
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (serverStatus === "Server is running") {
      resetConnectionErrorFlag();
    }
  }, [serverStatus]);

  useEffect(() => {
    if (!isUnderMaintenance && location.pathname === "/underMaintenance") {
      document.title = process.env.REACT_APP_NAME;
      navigate("/", { replace: true });
    }
  }, [isUnderMaintenance]);

  // Show loading while the server status is being checked
  if (isCheckingServerStatus) {
    return <CustomLoader showLoader="true" />; // Show loading until server status is confirmed
  }

  if (isUnderMaintenance) {
    document.title = "Under Maintenance"; // You can replace this with any desired title
    return (
      <>
        <UnderMaintenance />
        <Navigate to="/underMaintenance" replace />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route
          index
          element={isLoggedIn ? componentMap["/dashboard"] : <Login />}
        />
        {accessibleRoutes.length === 0 ? (
          <Route path="*" element={<Navigate to="/" replace />} />
        ) : (
          accessibleRoutes.map((route) => (
            <Route key={route} path={route} element={componentMap[route]} />
          ))
        )}

        <Route path="/underMaintenance" element={<UnderMaintenance />} />
        <Route path="/404" element={<NotFound />} />
        <Route
          path="*"
          element={<Navigate to={componentMap.noRoute} replace />}
        />
      </Routes>
    </>
  );
};

export default App;
